import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import { TriggerKey } from "@redotech/redo-model/advanced-flow/triggers";
import { CustomerEventType } from "@redotech/redo-model/customer-event/events/base";
import { SkillId } from "@redotech/redo-model/shopper-agent/skill-id";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum MarketingAgentDecisionType {
  SENTINEL = "sentinel",
  HERALD = "herald",
  VANGUARD = "vanguard",
  WRITER = "writer",
}

export enum SentinelDecisionAction {
  PROCEED = "proceed",
  SKIP = "skip",
  REEVALUATE_LATER = "reevaluate-later",
  BYPASSED = "bypassed",
}

// Sentinel Tag Enums - human-readable tags that answer "Why this decision?"

// PROCEED: Reasons to send the message now
export enum SentinelProceedTag {
  OPTIMAL_TIMING = "optimal_timing", // Good moment based on behavior/time patterns
  STRONG_PURCHASE_INTENT = "strong_purchase_intent", // Customer is ready to buy
  SESSION_ENDED_WITHOUT_PURCHASE = "session_ended_without_purchase", // They left without buying - nudge them back
  WIN_BACK_OPPORTUNITY = "win_back_opportunity", // Lapsed customer worth re-engaging
  WITHIN_CONTACT_LIMITS = "within_contact_limits", // Haven't over-messaged recently
  POST_PURCHASE_FOLLOW_UP = "post_purchase_follow_up", // Good timing for post-purchase engagement
  CAPTURE_INTEREST_NOW = "capture_interest_now", // Interest is peaking, act now
  CONVERSION_OPPORTUNITY = "conversion_opportunity", // Clear path to conversion
  OTHER = "other",
}

// SKIP: Reasons NOT to send
export enum SentinelSkipTag {
  LOW_PURCHASE_INTENT = "low_purchase_intent", // Not interested or not ready
  WOULD_HARM_EXPERIENCE = "would_harm_experience", // Would feel intrusive
  LET_THEM_CONVERT_NATURALLY = "let_them_convert_naturally", // Already on path to purchase
  ACTIVE_SESSION = "active_session", // Currently browsing - don't interrupt
  RECENTLY_PURCHASED = "recently_purchased", // Already bought recently
  TOO_EARLY_TO_REENGAGE = "too_early_to_reengage", // Not abandoned long enough
  OTHER = "other",
}

// REEVALUATE_LATER: Reasons to wait and check again
export enum SentinelReevaluateLaterTag {
  INTENT_STILL_FORMING = "intent_still_forming", // Wait for clearer purchase signals
  SESSION_IN_PROGRESS = "session_in_progress", // Wait until session ends, then decide
  CONTACTED_RECENTLY = "contacted_recently", // Respect messaging cooldown
  UNCLEAR_INTENT = "unclear_intent", // Need more behavioral data
  MAY_CONVERT_SOON = "may_convert_soon", // Watch for natural conversion first
  POST_PURCHASE_COOLDOWN = "post_purchase_cooldown", // Let recent purchase settle
  TIMING_NOT_OPTIMAL = "timing_not_optimal", // Better moment expected soon
  WAITING_FOR_TRIGGER = "waiting_for_trigger", // Waiting for specific event
  OTHER = "other",
}

// BYPASSED: Auto-tagged (no LLM decision needed)
export enum SentinelBypassedTag {
  BYPASSED_EVENT = "bypassed_event",
}

// Union type for all sentinel tags
export type SentinelTagValue =
  | SentinelProceedTag
  | SentinelSkipTag
  | SentinelReevaluateLaterTag
  | SentinelBypassedTag;

// All valid sentinel tags combined (for Mongoose enum validation)
export const allSentinelTags = [
  ...Object.values(SentinelProceedTag),
  ...Object.values(SentinelSkipTag),
  ...Object.values(SentinelReevaluateLaterTag),
  ...Object.values(SentinelBypassedTag),
] as const;

// Tag descriptions - typed to ensure all enum values have descriptions
export const sentinelProceedTagDescriptions: Record<
  SentinelProceedTag,
  string
> = {
  [SentinelProceedTag.OPTIMAL_TIMING]:
    "Good moment based on behavior/time patterns",
  [SentinelProceedTag.STRONG_PURCHASE_INTENT]: "Customer is ready to buy",
  [SentinelProceedTag.SESSION_ENDED_WITHOUT_PURCHASE]:
    "They left without buying - nudge them back",
  [SentinelProceedTag.WIN_BACK_OPPORTUNITY]:
    "Lapsed customer worth re-engaging",
  [SentinelProceedTag.WITHIN_CONTACT_LIMITS]: "Haven't over-messaged recently",
  [SentinelProceedTag.POST_PURCHASE_FOLLOW_UP]:
    "Good timing for post-purchase engagement",
  [SentinelProceedTag.CAPTURE_INTEREST_NOW]: "Interest is peaking, act now",
  [SentinelProceedTag.CONVERSION_OPPORTUNITY]: "Clear path to conversion",
  [SentinelProceedTag.OTHER]: "None of the above fit",
};

export const sentinelSkipTagDescriptions: Record<SentinelSkipTag, string> = {
  [SentinelSkipTag.LOW_PURCHASE_INTENT]: "Not interested or not ready",
  [SentinelSkipTag.WOULD_HARM_EXPERIENCE]: "Would feel intrusive",
  [SentinelSkipTag.LET_THEM_CONVERT_NATURALLY]: "Already on path to purchase",
  [SentinelSkipTag.ACTIVE_SESSION]: "Currently browsing - don't interrupt",
  [SentinelSkipTag.RECENTLY_PURCHASED]: "Already bought recently",
  [SentinelSkipTag.TOO_EARLY_TO_REENGAGE]: "Not abandoned long enough",
  [SentinelSkipTag.OTHER]: "None of the above fit",
};

export const sentinelReevaluateLaterTagDescriptions: Record<
  SentinelReevaluateLaterTag,
  string
> = {
  [SentinelReevaluateLaterTag.INTENT_STILL_FORMING]:
    "Wait for clearer purchase signals",
  [SentinelReevaluateLaterTag.SESSION_IN_PROGRESS]:
    "Wait until session ends, then decide",
  [SentinelReevaluateLaterTag.CONTACTED_RECENTLY]: "Respect messaging cooldown",
  [SentinelReevaluateLaterTag.UNCLEAR_INTENT]: "Need more behavioral data",
  [SentinelReevaluateLaterTag.MAY_CONVERT_SOON]:
    "Watch for natural conversion first",
  [SentinelReevaluateLaterTag.POST_PURCHASE_COOLDOWN]:
    "Let recent purchase settle",
  [SentinelReevaluateLaterTag.TIMING_NOT_OPTIMAL]:
    "Better moment expected soon",
  [SentinelReevaluateLaterTag.WAITING_FOR_TRIGGER]:
    "Waiting for specific event",
  [SentinelReevaluateLaterTag.OTHER]: "None of the above fit",
};

export const sentinelBypassedTagDescriptions: Record<
  SentinelBypassedTag,
  string
> = { [SentinelBypassedTag.BYPASSED_EVENT]: "Event was bypassed" };

// Combined descriptions for all sentinel tags
export const sentinelTagDescriptions: Record<SentinelTagValue, string> = {
  ...sentinelProceedTagDescriptions,
  ...sentinelSkipTagDescriptions,
  ...sentinelReevaluateLaterTagDescriptions,
  ...sentinelBypassedTagDescriptions,
};

// Sentinel decision types (discriminated by action field)
const sentinelProceedDecisionSchema = z.object({
  action: z.literal(SentinelDecisionAction.PROCEED),
  reasoning: z.string().optional(),
});
export const SentinelProceedDecision: z.ZodType<SentinelProceedDecision> =
  sentinelProceedDecisionSchema;
export interface SentinelProceedDecision
  extends z.infer<typeof sentinelProceedDecisionSchema> {}

const sentinelReevaluateLaterDecisionSchema = z.object({
  action: z.literal(SentinelDecisionAction.REEVALUATE_LATER),
  reasoning: z.string().optional(),
  reevaluateInMinutes: z.number(),
});
export const SentinelReevaluateLaterDecision: z.ZodType<SentinelReevaluateLaterDecision> =
  sentinelReevaluateLaterDecisionSchema;
export interface SentinelReevaluateLaterDecision
  extends z.infer<typeof sentinelReevaluateLaterDecisionSchema> {}

const sentinelSkipDecisionSchema = z.object({
  action: z.literal(SentinelDecisionAction.SKIP),
  reasoning: z.string().optional(),
});
export const SentinelSkipDecision: z.ZodType<SentinelSkipDecision> =
  sentinelSkipDecisionSchema;
export interface SentinelSkipDecision
  extends z.infer<typeof sentinelSkipDecisionSchema> {}

const sentinelBypassedDecisionSchema = z.object({
  action: z.literal(SentinelDecisionAction.BYPASSED),
  reasoning: z.string().optional(),
  bypassedEventType: createEnum(CustomerEventType, {
    enumName: "CustomerEventType",
  }),
});
export const SentinelBypassedDecision: z.ZodType<SentinelBypassedDecision> =
  sentinelBypassedDecisionSchema;
export interface SentinelBypassedDecision
  extends z.infer<typeof sentinelBypassedDecisionSchema> {}

const sentinelDecisionSchema = z.discriminatedUnion("action", [
  sentinelProceedDecisionSchema,
  sentinelReevaluateLaterDecisionSchema,
  sentinelSkipDecisionSchema,
  sentinelBypassedDecisionSchema,
]);
export const SentinelDecision: z.ZodType<SentinelDecision> =
  sentinelDecisionSchema;
export type SentinelDecision = z.infer<typeof sentinelDecisionSchema>;

// Vanguard decision type
export const vanguardChannelSchema = z.enum(["email", "sms"]);
export type VanguardChannel = z.infer<typeof vanguardChannelSchema>;

const prioritizedDetailContentSchema = z.object({
  product_id: z
    .string()
    .optional()
    .describe("The product ID, required if type=product"),
  product_url: z
    .string()
    .optional()
    .describe("The product URL, required if type=product"),
  description: z.string().describe("Description of this detail"),
  reason: z.string().describe("Why this matters for the customer"),
});
export const PrioritizedDetailContentSchema: z.ZodType<PrioritizedDetailContent> =
  prioritizedDetailContentSchema;
export interface PrioritizedDetailContent
  extends z.infer<typeof prioritizedDetailContentSchema> {}

const prioritizedDetailSchema = z.object({
  priority: z
    .enum(["high", "mid", "optional"])
    .describe("Priority level for this detail"),
  type: z
    .enum(["product", "timeline_reference", "insight"])
    .describe("Type of prioritized detail"),
  content: prioritizedDetailContentSchema,
});
export const PrioritizedDetailSchema: z.ZodType<PrioritizedDetail> =
  prioritizedDetailSchema;
export interface PrioritizedDetail
  extends z.infer<typeof prioritizedDetailSchema> {}

const discountOutputSchema = z.object({
  offer: z.boolean().describe("Whether to offer a discount"),
  percentage: z
    .number()
    .nullable()
    .describe("Discount percentage, if offering"),
  conversion_probability_boost: z
    .enum(["high", "moderate", "low"])
    .nullable()
    .describe("Expected boost to conversion probability"),
  reasoning: z.string().optional().describe("Reasoning for discount decision"),
});
export const DiscountOutputSchema: z.ZodType<DiscountOutput> =
  discountOutputSchema;
export interface DiscountOutput extends z.infer<typeof discountOutputSchema> {}

const vanguardDecisionSchema = z.object({
  channel: vanguardChannelSchema.describe("Selected outreach channel"),
  prioritized_details: z
    .array(prioritizedDetailSchema)
    .describe("Prioritized details for the writing agent"),
  discount: discountOutputSchema
    .optional()
    .describe("Discount strategy, only present when offering a discount"),
  message_strategy: z.string().describe("Strategic direction for the message"),
  reasoning: z.string().optional().describe("Your thinking process"),
});
export const VanguardDecision: z.ZodType<VanguardDecision> =
  vanguardDecisionSchema;
export interface VanguardDecision
  extends z.infer<typeof vanguardDecisionSchema> {}

// Herald decision type — picks channel + active skill set for the trigger.
// Runs between Sentinel and Vanguard in the new coordinator path.
const skillIdSchema = createEnum(SkillId, { enumName: "SkillId" });
const validSkillIds = new Set<string>(Object.values(SkillId));
const storedSelectedSkillsSchema = z
  .array(z.string())
  .transform((skillIds) =>
    skillIds.filter((skillId): skillId is SkillId =>
      validSkillIds.has(skillId),
    ),
  );

const heraldDecisionSchema = z.object({
  channel: createEnum(MarketingChannel, {
    enumName: "MarketingChannel",
  }).describe("Selected outreach channel"),
  selectedSkills: z
    .array(skillIdSchema)
    .describe("SkillIds active for this vanguard loop run"),
});
export const HeraldDecision: z.ZodType<HeraldDecision> = heraldDecisionSchema;
export interface HeraldDecision extends z.infer<typeof heraldDecisionSchema> {}

const storedHeraldDecisionSchema = heraldDecisionSchema.extend({
  selectedSkills: storedSelectedSkillsSchema.describe(
    "SkillIds active for this vanguard loop run",
  ),
});

// Writer decision types (discriminated by channel)
const smsWriterContentSchema = z.object({
  channel: z.literal("sms"),
  message: z
    .string()
    .describe(
      "The SMS message (one sentence, 160 chars ideal, 300 max with link)",
    ),
  reasoning: z
    .string()
    .optional()
    .describe("Your approach to writing this message"),
});
export const SmsWriterContent: z.ZodType<SmsWriterContent> =
  smsWriterContentSchema;
export interface SmsWriterContent
  extends z.infer<typeof smsWriterContentSchema> {}

const emailWriterContentSchema = z.object({
  channel: z.literal("email"),
  subject: z.string().describe("The email subject line"),
  preview: z.string().describe("The email preview text"),
  body: z
    .string()
    .describe("The email body content (plain text with basic formatting)"),
  reasoning: z
    .string()
    .optional()
    .describe("Your approach to writing this email"),
});
export const EmailWriterContent: z.ZodType<EmailWriterContent> =
  emailWriterContentSchema;
export interface EmailWriterContent
  extends z.infer<typeof emailWriterContentSchema> {}

const writerDecisionSchema = z.discriminatedUnion("channel", [
  smsWriterContentSchema,
  emailWriterContentSchema,
]);
export const WriterDecision: z.ZodType<WriterDecision> = writerDecisionSchema;
export type WriterDecision = z.infer<typeof writerDecisionSchema>;

const vanguardV2DecisionSchema = z.object({ version: z.literal("v2") });
export const VanguardV2Decision: z.ZodType<VanguardV2Decision> =
  vanguardV2DecisionSchema;
export interface VanguardV2Decision
  extends z.infer<typeof vanguardV2DecisionSchema> {}

// Union over v1 (legacy, no version field) and v2 (version: "v2").
// Writer code must branch on `"version" in data && data.version === "v2"`.
const vanguardDecisionUnionSchema = z.union([
  vanguardV2DecisionSchema,
  vanguardDecisionSchema,
]);
export const VanguardDecisionUnion: z.ZodType<VanguardDecisionUnion> =
  vanguardDecisionUnionSchema;
export type VanguardDecisionUnion = z.infer<typeof vanguardDecisionUnionSchema>;

// Trigger types
export enum MarketingAgentDecisionTriggerType {
  EVENT = "event",
  DECISION = "decision",
}

export const OrderTrackingEventReference = z.object({
  orderId: z.string(),
  trackingCode: z.string().nullable().optional(),
  fulfillmentId: z.string().nullable().optional(),
});
export type OrderTrackingEventReference = z.infer<
  typeof OrderTrackingEventReference
>;

// Event trigger - triggered by a customer event or order-tracking event
const eventTriggerSchema = z.object({
  type: z.literal(MarketingAgentDecisionTriggerType.EVENT),
  eventId: z.string().optional(),
  orderTrackingEvent: OrderTrackingEventReference.optional(),
  eventType: createEnum(CustomerEventType, { enumName: "CustomerEventType" }),
  triggerKey: z.string().transform((s) => s as TriggerKey),
  skipBuffer: z.boolean().optional(),
  skipSentinel: z.boolean().optional(),
  demoMode: z.boolean().optional(),
});
export const EventTrigger: z.ZodType<EventTrigger> = eventTriggerSchema;
export interface EventTrigger extends z.infer<typeof eventTriggerSchema> {}

// Decision trigger - triggered by a previous decision (e.g., after cooldown)
const decisionTriggerSchema = z.object({
  type: z.literal(MarketingAgentDecisionTriggerType.DECISION),
  decisionId: zExt.objectId(),
  decisionType: createEnum(MarketingAgentDecisionType, {
    enumName: "MarketingAgentDecisionType",
  }),
});
export const DecisionTrigger: z.ZodType<DecisionTrigger> =
  decisionTriggerSchema;
export interface DecisionTrigger
  extends z.infer<typeof decisionTriggerSchema> {}

// Union of all trigger types
const marketingAgentDecisionTriggerSchema = z.discriminatedUnion("type", [
  eventTriggerSchema,
  decisionTriggerSchema,
]);
export const MarketingAgentDecisionTrigger: z.ZodType<MarketingAgentDecisionTrigger> =
  marketingAgentDecisionTriggerSchema;
export type MarketingAgentDecisionTrigger = z.infer<
  typeof marketingAgentDecisionTriggerSchema
>;

// Base schema (common fields for all decision types)
const baseMarketingAgentDecisionSchema = z.object({
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
  customerId: zExt.objectId(),
  // Nullish because SENTINEL bypass decisions don't create a thread (no LLM call)
  threadId: zExt.objectId().nullish(),
  trigger: marketingAgentDecisionTriggerSchema,
  triggeredAt: z.date(),
  finishedAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const sentinelTagSchema = z.enum(allSentinelTags);

// Sentinel type schema
const sentinelMarketingAgentDecisionSchema =
  baseMarketingAgentDecisionSchema.extend({
    type: z.literal(MarketingAgentDecisionType.SENTINEL),
    data: sentinelDecisionSchema,
    tags: z.array(sentinelTagSchema).optional(),
  });
export const SentinelMarketingAgentDecisionSchema: z.ZodType<SentinelMarketingAgentDecision> =
  sentinelMarketingAgentDecisionSchema;
export interface SentinelMarketingAgentDecision
  extends z.infer<typeof sentinelMarketingAgentDecisionSchema> {}

// Herald type schema
const heraldMarketingAgentDecisionSchema =
  baseMarketingAgentDecisionSchema.extend({
    type: z.literal(MarketingAgentDecisionType.HERALD),
    data: storedHeraldDecisionSchema,
  });
export const HeraldMarketingAgentDecisionSchema: z.ZodType<HeraldMarketingAgentDecision> =
  heraldMarketingAgentDecisionSchema;
export interface HeraldMarketingAgentDecision
  extends z.infer<typeof heraldMarketingAgentDecisionSchema> {}

// Vanguard type schema — data is a union of v1 (legacy) and v2 (new loop).
const vanguardMarketingAgentDecisionSchema =
  baseMarketingAgentDecisionSchema.extend({
    type: z.literal(MarketingAgentDecisionType.VANGUARD),
    data: vanguardDecisionUnionSchema,
  });
export const VanguardMarketingAgentDecisionSchema: z.ZodType<VanguardMarketingAgentDecision> =
  vanguardMarketingAgentDecisionSchema;
export interface VanguardMarketingAgentDecision
  extends z.infer<typeof vanguardMarketingAgentDecisionSchema> {}

// Writer type schema
const writerMarketingAgentDecisionSchema =
  baseMarketingAgentDecisionSchema.extend({
    type: z.literal(MarketingAgentDecisionType.WRITER),
    data: writerDecisionSchema,
  });
export const WriterMarketingAgentDecisionSchema: z.ZodType<WriterMarketingAgentDecision> =
  writerMarketingAgentDecisionSchema;
export interface WriterMarketingAgentDecision
  extends z.infer<typeof writerMarketingAgentDecisionSchema> {}

// Discriminated union of all decision types
const marketingAgentDecisionSchema = z.discriminatedUnion("type", [
  sentinelMarketingAgentDecisionSchema,
  heraldMarketingAgentDecisionSchema,
  vanguardMarketingAgentDecisionSchema,
  writerMarketingAgentDecisionSchema,
]);
export const MarketingAgentDecisionSchema: z.ZodType<MarketingAgentDecision> =
  marketingAgentDecisionSchema;
export type MarketingAgentDecision = z.infer<
  typeof marketingAgentDecisionSchema
>;
